import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { FaHome, FaRegCalendarAlt, FaRegUser, FaCog, FaUserCircle, FaRegClone } from 'react-icons/fa';
import { RiRestaurantFill } from "react-icons/ri";
import './BottomNavbar.css';

/**
 * Array of navigation items rendered in the bottom bar.
 * `icon` is a react-icons component; `routePrefix` is used for active
 * detection (a `startsWith` check).
 *
 * @constant {Array<{key: string, icon: React.ComponentType, route: string, routePrefixes: string[], translationKey: string}>}
 */
const NAV_ITEMS = [
    {
        key: 'home',
        icon: FaHome,
        route: '/home',
        routePrefixes: ['/home'],
        translationKey: 'Home',
    },
    {
        key: 'calendar',
        icon: FaRegCalendarAlt,
        route: '/calendar',
        routePrefixes: ['/calendar'],
        translationKey: 'Calendar',
    },
    {
        key: 'clients',
        icon: FaRegUser,
        route: '/clients',
        routePrefixes: ['/clients', '/client/'],
        translationKey: 'Clients',
    },
    {
        key: 'foods',
        icon: RiRestaurantFill,
        route: '/foods',
        routePrefixes: ['/foods', '/food/'],
        translationKey: 'Foods',
    },
    {
        key: 'templates',
        icon: FaRegClone,
        route: '/templates',
        routePrefixes: ['/templates'],
        translationKey: 'Templates',
    },
    {
        key: 'profile',
        icon: FaUserCircle,
        route: '/profile',
        routePrefixes: ['/profile'],
        translationKey: 'profile',
    },
    {
        key: 'settings',
        icon: FaCog,
        route: '/settings',
        routePrefixes: ['/settings'],
        translationKey: 'Settings',
    },
];

/**
 * BottomNavbar component.
 *
 * A fixed bottom navigation bar rendered on all authenticated pages.
 * Visible only on small screens (≤ 768 px) via CSS; the sidebar is hidden
 * at the same breakpoint so the two never appear simultaneously.
 *
 * Contains four navigation items: Home, Calendar, Clients, Settings.
 * The active item is highlighted in the app's primary colour (#fd4701).
 *
 * @component
 * @returns {JSX.Element}
 */
const BottomNavbar = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();

    /**
     * Returns true if the given item should be considered active for the
     * current URL pathname.
     *
     * @param {string[]} prefixes - Route prefixes to test.
     * @returns {boolean}
     */
    const isActive = (prefixes) =>
        prefixes.some(prefix => location.pathname.startsWith(prefix));

    return (
        <>
        <nav className="bnb" aria-label="bottom navigation">
            {NAV_ITEMS.map(({ key, icon: Icon, route, routePrefixes, translationKey }) => (
                <button
                    key={key}
                    className={`bnb-item${isActive(routePrefixes) ? ' bnb-item--active' : ''}`}
                    onClick={() => navigate(route)}
                    aria-label={t(translationKey)}
                    aria-current={isActive(routePrefixes) ? 'page' : undefined}
                >
                    <Icon className="bnb-icon" />
                    <span className="bnb-label">{t(translationKey)}</span>
                </button>
            ))}

        </nav>
        </>
    );
};

export default BottomNavbar;
